import authService from './authService';
import {
  MaterialReceipt,
  MaterialReceiptFormData,
  MaterialReceiptSearchModel,
  MaterialReceiptInstallmentRow,
  MaterialReceiptItemRow,
  MaterialReceiptItemDamagePhoto,
  MaterialReceiptItemReceivedPhoto,
  PurchaseOrderForReceipt,
  UpdateMaterialReceiptStatusRequest,
  CustomerOrderForReceipt,
  MaterialReceiptOrderUpdate,
  GenerateReturnInvoicePayload,
} from '../types/materialReceipt';
import { ProviderProduct } from '../types/purchaseOrder';
import { PaginatedResponse, ApiResponse } from '../types/common';

// ─── Service ─────────────────────────────────────────────────────

class MaterialReceiptService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ─── Material Receipts ──────────────────────────────────────────

  async getMaterialReceipts(
    searchModel: MaterialReceiptSearchModel,
  ): Promise<PaginatedResponse<MaterialReceipt>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchName && { search: searchModel.searchName }),
      ...(searchModel.provider_id !== undefined && { provider_id: searchModel.provider_id.toString() }),
      ...(searchModel.customer_id !== undefined && { customer_id: searchModel.customer_id.toString() }),
      ...(searchModel.status !== undefined && { status: searchModel.status.toString() }),
      ...(searchModel.date_from && { date_from: searchModel.date_from }),
      ...(searchModel.date_to && { date_to: searchModel.date_to }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/material-receipts?${params}`);
    if (!response.ok) throw new Error('Failed to fetch material receipts');

    const json = await response.json();
    // A paginação vem em snake_case no NÍVEL RAIZ da resposta ({ success, data,
    // pagination }). Lendo só json.data.pagination, total_pages caía no default 1
    // e a tela nunca oferecia a página seguinte — os recebimentos antigos ficavam
    // inalcançáveis. O caminho aninhado fica como fallback.
    const pagination = json.pagination ?? json.data?.pagination;
    return {
      success: json.success,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: pagination?.page ?? searchModel.page,
        limit: pagination?.page_size ?? searchModel.pageSize,
        total: pagination?.total_items ?? 0,
        totalPages: pagination?.total_pages ?? 1,
      },
    };
  }

  async getMaterialReceiptById(id: number): Promise<MaterialReceipt> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/material-receipts/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Recebimento de material não encontrado');
      throw new Error('Failed to fetch material receipt');
    }
    const data: ApiResponse<MaterialReceipt> = await response.json();
    return data.data!;
  }

  async createMaterialReceipt(formData: MaterialReceiptFormData): Promise<MaterialReceipt> {
    const apiClient = await this.getApiClient();
    const payload = {
      ...formData,
      items: formData.items.map(i => this.sanitizeItem(i)),
      installments: formData.installments.map(i => this.sanitizeInstallment(i)),
    };
    const response = await apiClient.post('/material-receipts', payload);
    if (!response.ok) throw new Error('Failed to create material receipt');
    const data: ApiResponse<MaterialReceipt> = await response.json();
    return data.data!;
  }

  async updateMaterialReceipt(id: number, formData: Partial<MaterialReceiptFormData>): Promise<MaterialReceipt> {
    const apiClient = await this.getApiClient();
    const payload = {
      ...formData,
      ...(formData.items && { items: formData.items.map(i => this.sanitizeItem(i)) }),
      ...(formData.installments && { installments: formData.installments.map(i => this.sanitizeInstallment(i)) }),
    };
    const response = await apiClient.put(`/material-receipts/${id}`, payload);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Recebimento de material não encontrado');
      if (response.status === 409) throw new Error('Recebimento já confirmado e não pode ser alterado');
      throw new Error('Failed to update material receipt');
    }
    const data: ApiResponse<MaterialReceipt> = await response.json();
    return data.data!;
  }

  async deleteMaterialReceipt(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/material-receipts/${id}`);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Recebimento de material não encontrado');
      throw new Error('Failed to delete material receipt');
    }
  }

  async updateMaterialReceiptStatus(
    id: number,
    body: UpdateMaterialReceiptStatusRequest,
  ): Promise<MaterialReceipt> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/material-receipts/${id}/status`, body);
    if (!response.ok) {
      if (response.status === 404) throw new Error('Recebimento de material não encontrado');
      const errorBody = await response.json().catch(() => ({}));
      const msg = errorBody?.message || errorBody?.error || 'Erro ao atualizar status do recebimento';
      throw new Error(msg);
    }
    const data: ApiResponse<MaterialReceipt> = await response.json();
    return data.data!;
  }

  // ─── Purchase Orders by provider (status 0 or 3) ───────────────

  async getPurchaseOrdersByProvider(providerId: number): Promise<PurchaseOrderForReceipt[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/provider/${providerId}/purchase-orders`,
    );
    if (!response.ok) throw new Error('Failed to fetch purchase orders for provider');
    const data: ApiResponse<PurchaseOrderForReceipt[]> = await response.json();
    return data.data ?? [];
  }

  // ─── Validate PO for item ──────────────────────────────────────

  async validatePurchaseOrderForItem(
    providerId: number,
    purchaseOrderId: number,
    productId?: number,
    rawMaterialId?: number,
  ): Promise<boolean> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({
      provider_id: providerId.toString(),
      purchase_order_id: purchaseOrderId.toString(),
      ...(productId && { product_id: productId.toString() }),
      ...(rawMaterialId && { raw_material_id: rawMaterialId.toString() }),
    });
    const response = await apiClient.get(`/material-receipts/validate-purchase-order?${params}`);
    if (!response.ok) return false;
    const data: ApiResponse<{ valid: boolean }> = await response.json();
    return data.data?.valid ?? false;
  }

  // ─── Search products by provider (reuse) ───────────────────────

  async searchProductsByProvider(providerId: number, query?: string): Promise<ProviderProduct[]> {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${providerId}/purchase-products?${params}`);
    if (!response.ok) throw new Error('Failed to search products');
    const data: ApiResponse<ProviderProduct[]> = await response.json();
    return data.data ?? [];
  }

  // ─── Report damage + optional photo upload ────────────────────

  /**
   * Registra (ou atualiza) uma avaria num item já persistido, enviando
   * a foto para o Azure Blob Storage via o endpoint multipart do backend.
   *
   * Endpoint: POST /api/material-receipts/:receiptId/damage
   * Content-Type: multipart/form-data
   * Campos: item_id, damage_notes, damage_photo (opcional)
   */
  async reportDamage(
    receiptId: number,
    itemId: number,
    damageNotes: string,
    photo?: File | null,
  ): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('item_id', String(itemId));
    fd.append('damage_notes', damageNotes);
    if (photo) fd.append('damage_photo', photo);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/damage`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    // 1ª tentativa — apenas cookie de sessão
    let response = await doFetch();
    if (response.status === 401) {
      // 2ª tentativa — Bearer token em cache
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Failed to report damage: ${response.status} ${body}`);
    }
  }

  // ─── Get damage photo info ─────────────────────────────────────

  /**
   * Busca as informações de avaria de um item específico (URL da foto, notas).
   * Recomendado para recarregar a URL depois de um upload recém-concluído.
   *
   * Endpoint: GET /api/material-receipts/:receiptId/items/:itemId/damage-info
   */
  async getDamageInfo(
    receiptId: number,
    itemId: number,
  ): Promise<{ damage_notes?: string; damage_photo_url?: string }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/${receiptId}/items/${itemId}/damage-info`,
    );
    if (!response.ok) throw new Error('Failed to fetch damage info');
    const data: ApiResponse<{ damage_notes?: string; damage_photo_url?: string }> =
      await response.json();
    return data.data ?? {};
  }

  // ─── Get damage photo binary (proxy endpoint) ──────────────────

  /**
   * Busca o binário da foto de avaria via endpoint proxy seguro da API.
   * O Azure Blob Storage não é público — nunca use damage_photo_url diretamente
   * como src de <img>. Use este método e passe o object URL retornado.
   *
   * Retorna um object URL (blob:...) que deve ser revogado via
   * URL.revokeObjectURL() quando não for mais necessário.
   *
   * Endpoint: GET /api/material-receipts/:receiptId/items/:itemId/damage-photo
   */
  async getDamagePhoto(receiptId: number, itemId: number): Promise<string> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/items/${itemId}/damage-photo`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch damage photo: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─── Damage Photos (multiple) ──────────────────────────────────

  /**
   * Lista as fotos de avaria de um item de recebimento.
   * Endpoint: GET /api/material-receipts/:receiptId/items/:itemId/damage-photos
   */
  async getDamagePhotos(receiptId: number, itemId: number): Promise<MaterialReceiptItemDamagePhoto[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/${receiptId}/items/${itemId}/damage-photos`,
    );
    if (!response.ok) throw new Error('Failed to fetch damage photos');
    const data: ApiResponse<MaterialReceiptItemDamagePhoto[]> = await response.json();
    return data.data ?? [];
  }

  /**
   * Faz upload de uma foto de avaria para um item de recebimento.
   * Endpoint: POST /api/material-receipts/:receiptId/items/:itemId/damage-photos
   * Content-Type: multipart/form-data
   */
  async uploadDamagePhoto(
    receiptId: number,
    itemId: number,
    photo: File,
  ): Promise<MaterialReceiptItemDamagePhoto> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('damage_photo', photo);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/items/${itemId}/damage-photos`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Failed to upload damage photo: ${response.status} ${body}`);
    }
    const data = await response.json();
    return data.data;
  }

  /**
   * Remove uma foto de avaria (também deleta do Azure Blob Storage).
   * Endpoint: DELETE /api/material-receipts/:receiptId/items/:itemId/damage-photos/:photoId
   */
  async deleteDamagePhoto(
    receiptId: number,
    itemId: number,
    photoId: number,
  ): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(
      `/material-receipts/${receiptId}/items/${itemId}/damage-photos/${photoId}`,
    );
    if (!response.ok) throw new Error('Failed to delete damage photo');
  }

  /**
   * Busca o binário de uma foto de avaria específica via proxy.
   * Endpoint: GET /api/material-receipts/:receiptId/items/:itemId/damage-photos/:photoId/file
   */
  async getDamagePhotoFile(receiptId: number, itemId: number, photoId: number): Promise<string> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/items/${itemId}/damage-photos/${photoId}/file`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch damage photo file: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─── Fotos do recebimento (produto personalizado) ──────────────
  // Espelham os métodos de damage-photos; o blob é privado (proxy p/ exibir).

  async getReceivedPhotos(receiptId: number, itemId: number): Promise<MaterialReceiptItemReceivedPhoto[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/${receiptId}/items/${itemId}/received-photos`,
    );
    if (!response.ok) throw new Error('Failed to fetch received photos');
    const data: ApiResponse<MaterialReceiptItemReceivedPhoto[]> = await response.json();
    return data.data ?? [];
  }

  /** Upload multipart (campo received_photo) de uma foto do produto recebido. */
  async uploadReceivedPhoto(
    receiptId: number,
    itemId: number,
    photo: File,
  ): Promise<MaterialReceiptItemReceivedPhoto> {
    const baseUrl = await this.getBaseUrl();
    const fd = new FormData();
    fd.append('received_photo', photo);

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/items/${itemId}/received-photos`, {
        method: 'POST',
        body: fd,
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Failed to upload received photo: ${response.status} ${body}`);
    }
    const data = await response.json();
    return data.data;
  }

  async deleteReceivedPhoto(receiptId: number, itemId: number, photoId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(
      `/material-receipts/${receiptId}/items/${itemId}/received-photos/${photoId}`,
    );
    if (!response.ok) throw new Error('Failed to delete received photo');
  }

  /** Busca o binário via proxy e devolve um object URL (revogar após o uso). */
  async getReceivedPhotoFile(receiptId: number, itemId: number, photoId: number): Promise<string> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${receiptId}/items/${itemId}/received-photos/${photoId}/file`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch received photo file: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─── Register Damage (new flow) ────────────────────────────────

  /**
   * Registra uma avaria para um item, criando um novo MaterialReceiptItem com Damaged=1.
   * Endpoint: POST /api/material-receipts/:receiptId/items/:itemId/register-damage
   * Body: { quantity: number, damage_notes: string }
   */
  async registerDamage(
    receiptId: number,
    itemId: number,
    quantity: number,
    damageNotes: string,
  ): Promise<MaterialReceiptItemRow> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/material-receipts/${receiptId}/items/${itemId}/register-damage`,
      { quantity, damage_notes: damageNotes },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erro ao registrar avaria');
    }
    const data = await response.json();
    return data.data;
  }

  /**
   * Atualiza uma avaria existente (quantidade e/ou descrição).
   * Endpoint: PUT /api/material-receipts/:receiptId/items/:itemId/damage
   * Body: { quantity?: number, damage_notes?: string }
   */
  async updateDamage(
    receiptId: number,
    itemId: number,
    quantity: number,
    damageNotes: string,
  ): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(
      `/material-receipts/${receiptId}/items/${itemId}/damage`,
      { quantity, damage_notes: damageNotes },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erro ao atualizar avaria');
    }
  }

  /**
   * Remove uma avaria (exclui o item de avaria e suas fotos do Azure).
   * Endpoint: DELETE /api/material-receipts/:receiptId/items/:itemId/damage
   */
  async removeDamage(receiptId: number, itemId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(
      `/material-receipts/${receiptId}/items/${itemId}/damage`,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erro ao remover avaria');
    }
  }

  // ─── Split item ────────────────────────────────────────────────

  async splitItem(
    receiptId: number,
    itemId: number,
    splitQuantity: number,
  ): Promise<{ original: MaterialReceiptItemRow; newItem: MaterialReceiptItemRow }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/material-receipts/${receiptId}/items/${itemId}/split`,
      { split_quantity: splitQuantity },
    );
    if (!response.ok) throw new Error('Failed to split item');
    const data = await response.json();
    return data.data;
  }

  // ─── Assign purchase orders to items ───────────────────────────

  async assignPurchaseOrders(
    receiptId: number,
    assignments: { item_id: number; purchase_order_id: number | null }[],
  ): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/material-receipts/${receiptId}/assign-purchase-orders`,
      { assignments },
    );
    if (!response.ok) throw new Error('Failed to assign purchase orders');
  }

  // ─── Suggest purchase orders for items ─────────────────────────

  async suggestPurchaseOrders(
    receiptId: number,
  ): Promise<
    {
      item_id: number;
      description: string;
      code: string;
      quantity: number;
      suggestions: {
        purchase_order_id: number;
        order_number?: number;
        order_date?: string;
        item_quantity: number;
      }[];
    }[]
  > {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/${receiptId}/suggest-purchase-orders`,
    );
    if (!response.ok) throw new Error('Failed to suggest purchase orders');
    const data = await response.json();
    return data.data ?? [];
  }

  // ─── PDF ──────────────────────────────────────────────────────

  async downloadPdf(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${id}/pdf`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `recebimento-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async downloadLabels(id: number): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/material-receipts/${id}/labels`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar etiquetas (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `etiquetas-recebimento-${id}.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ─── Customer orders for receipt (devoluções) ──────────────────

  /**
   * Busca pedidos de venda de um cliente para seleção de itens de devolução.
   * Retorna pedidos com itens e itens de reposição.
   *
   * Endpoint: GET /api/v1/material-receipts/customer/:customerId/orders
   */
  async getCustomerOrders(customerId: number): Promise<CustomerOrderForReceipt[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/material-receipts/customer/${customerId}/orders`);
    if (!response.ok) throw new Error('Erro ao buscar pedidos do cliente');
    const data: ApiResponse<CustomerOrderForReceipt[]> = await response.json();
    return data.data ?? [];
  }

  /**
   * Busca pedidos de venda de um cliente que contenham um produto específico
   * com quantidade disponível para devolução (Quantity > ReturnedQuantity).
   */
  async getCustomerOrdersByProduct(customerId: number, productId: number): Promise<CustomerOrderForReceipt[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/material-receipts/customer/${customerId}/orders?product_id=${productId}&available_for_return=true`,
    );
    if (!response.ok) throw new Error('Erro ao buscar pedidos do cliente para este produto');
    const data: ApiResponse<CustomerOrderForReceipt[]> = await response.json();
    return data.data ?? [];
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  // ─── Payload sanitizers ────────────────────────────────────────

  /**
   * Strips frontend-only and backend-readonly fields from an item before
   * sending to the API. Includes material_receipt_item_id when present so
   * the backend can UPDATE the existing row instead of deleting and recreating it.
   */
  private sanitizeItem(item: MaterialReceiptItemRow) {
    return {
      ...(item.material_receipt_item_id ? { material_receipt_item_id: item.material_receipt_item_id } : {}),
      description: item.description,
      unit: item.unit,
      unit_value: item.unit_value,
      quantity: item.quantity,
      product_id: item.product_id,
      raw_material_id: item.raw_material_id,
      purchase_order_id: item.purchase_order_id,
      order_id: item.order_id,
      damaged: item.damaged,
      damage_notes: item.damage_notes,
      // sku, supplier_code, damage_photo_url — backend-managed, never sent
    };
  }

  /**
   * Strips frontend-only and backend-readonly fields from an installment
   * before sending to the API.
   */
  private sanitizeInstallment(inst: MaterialReceiptInstallmentRow) {
    return {
      ...(inst.material_receipt_installment_id ? { material_receipt_installment_id: inst.material_receipt_installment_id } : {}),
      value: inst.value,
      due_date: inst.due_date,
      notes: inst.notes,
      payment_method_id: inst.payment_method_id,
      credit_card_id: inst.credit_card_id ?? undefined,
      // payment_method_name — backend-managed, never sent
    };
  }

  // ─── Material Receipt Order Updates (Financial/Fiscal Management) ──

  /**
   * Busca o status de atualizações financeiras/fiscais dos pedidos de venda vinculados a um recebimento.
   * GET /api/material-receipts/:receiptId/order-updates
   */
  async getOrderUpdates(receiptId: number): Promise<MaterialReceiptOrderUpdate[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/material-receipts/${receiptId}/order-updates`);
    if (!response.ok) throw new Error('Erro ao buscar atualizações de pedidos');
    const data: ApiResponse<MaterialReceiptOrderUpdate[]> = await response.json();
    return data.data ?? [];
  }

  /**
   * Aciona o estorno financeiro de um pedido vinculado ao recebimento.
   * POST /api/material-receipts/:receiptId/orders/:orderId/financial-reversal
   */
  async applyFinancialReversal(receiptId: number, orderId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/material-receipts/${receiptId}/orders/${orderId}/financial-reversal`,
      {},
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erro ao aplicar estorno financeiro');
    }
  }

  /**
   * Aciona a geração da nota fiscal de devolução referente ao pedido de venda.
   * POST /api/material-receipts/:receiptId/orders/:orderId/return-invoice
   */
  async generateReturnInvoice(receiptId: number, orderId: number, payload: GenerateReturnInvoicePayload): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/material-receipts/${receiptId}/orders/${orderId}/return-invoice`,
      payload,
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || 'Erro ao gerar nota de devolução');
    }
  }
}

// Singleton instance
export const materialReceiptService = new MaterialReceiptService();
export default materialReceiptService;
